import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage, useIntl } from 'react-intl';
import { unwrapResult } from '@reduxjs/toolkit';
import { useSnackbar } from 'notistack';
import DateTime from '../../../../utils/DateTime';
import {
  Feature,
  getFeatureByAddOn,
  getProductId,
  getRecurlySubUuIdPathParam,
  isTestCloudFeature,
  PlanInterval,
  RecurlyAddOn,
  SecondarySource,
  Subscription,
  SubscriptionSource,
} from '../../../../models';
import { ToggleRenewal } from '../TurnOffAutoRenew/ToggleRenewal';
import { ToggleRenewalConfirmDialog } from '../TurnOffAutoRenew/ToggleRenewalConfirmDialog';
import CustomSwitch from '../../../../components/switch/CustomSwitch';
import { plan } from '../../../../layout/routes';
import { useNavigate, useQuery } from '../../../../routes';
import { fromSubscription, useAppDispatch } from '../../../../store';
import { TrackingPayload } from '../../../../utils/utils';
import { isTrialSubscription } from '../../subscriptionTrial';
import { getDisplayQuota } from '../../subscriptionQuota';
import { PLAN_SUBSCRIPTION_ID_QUERY_PARAM } from '../../../../utils/Constants';
import { isSelfServePaidSubscription } from '../../../../utils/subscriptionTarget';
import getFeaturePrefix = Feature.getFeaturePrefix;

export const SUBSCRIPTION_ROW_GRID = 'minmax(230px, 2.5fr) 1.2fr 0.95fr 1.05fr 148px';

const useStyles = makeStyles(theme => ({
  rowContainer: {
    borderTop: '1px solid #EEF2F0',
    '&:first-of-type': {
      borderTop: 'none',
    },
  },
  rowGrid: {
    display: 'grid',
    gridTemplateColumns: SUBSCRIPTION_ROW_GRID,
    columnGap: theme.spacing(1.75),
    alignItems: 'center',
    padding: theme.spacing(3, 3.25),
  },
  productCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.75),
    minWidth: 0,
  },
  // Design CSS says 400, but the design file only loads Inter Tight 500+ — the browser
  // falls back to the 500 face, so 500 is what the design actually renders.
  productName: {
    fontSize: 18,
    fontWeight: 500,
    color: '#16141D',
  },
  productSubtitle: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.75),
  },
  seatLabel: {
    fontSize: 13,
    color: '#5B6661',
    whiteSpace: 'nowrap',
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    minHeight: 20,
    padding: theme.spacing(0, 1.125),
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
  },
  paidPill: {
    backgroundColor: '#DCF1E8',
    color: '#0C1411',
  },
  trialPill: {
    backgroundColor: '#FDF6E3',
    color: '#5A4A00',
  },
  legacyPill: {
    backgroundColor: '#EEF2F0',
    color: '#3C4642',
  },
  managedPill: {
    backgroundColor: '#EEF2F0',
    color: '#3C4642',
  },
  expandButton: {
    flex: 'none',
    width: 30,
    height: 30,
    borderRadius: 8,
    border: '1px solid #E1E7E4',
    backgroundColor: '#fff',
    color: '#5F5D75',
    transition: 'background .15s ease',
    '&:hover': {
      backgroundColor: '#F4F3F9',
      color: '#16141D',
    },
  },
  renewalDate: {
    fontSize: 14,
    fontWeight: 500,
    color: '#16141D',
  },
  // Days-remaining warning — only rendered when the renewal is close (≤ 60 days).
  renewalDays: {
    marginTop: theme.spacing(0.375),
    fontSize: 12.5,
    fontWeight: 600,
    color: '#8A7218',
  },
  cycleValue: {
    fontSize: 14,
    fontWeight: 500,
    color: '#16141D',
  },
  autoRenewCell: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
  },
  autoRenewState: {
    fontSize: 13,
    minWidth: 20,
  },
  lockIcon: {
    display: 'inline-flex',
    color: '#5B6661',
    fontSize: 15,
  },
  // Shared secondary action button (Switch to annual / Add seats / Contact sales).
  actionBtn: {
    justifySelf: 'end',
    width: 140,
    height: 36,
    boxSizing: 'border-box' as const,
    borderRadius: '9px',
    border: '1.5px solid #E1E7E4',
    backgroundColor: '#fff',
    color: '#16141D',
    fontSize: 12.5,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    textTransform: 'none' as const,
    '&:hover': {
      backgroundColor: '#F0FAF5',
      border: '1.5px solid #9FCEC0',
    },
  },
  noteWrap: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.125),
    margin: theme.spacing(0, 3.25, 2.5),
    padding: theme.spacing(1.375, 1.75),
    borderRadius: 11,
  },
  noteInfo: {
    border: '1px solid #E1E7E4',
    backgroundColor: '#F7F9F8',
  },
  noteWarn: {
    border: '1px solid #F2DDB4',
    backgroundColor: '#FDF8EC',
  },
  noteText: {
    fontSize: 13,
    lineHeight: 1.5,
    color: '#3C3A50',
  },
  noteHelp: {
    color: '#5F5D75',
  },
  noteHelpLink: {
    color: '#0F8461',
    fontWeight: 600,
    textDecoration: 'none',
  },
  reEnableLink: {
    border: 'none',
    background: 'none',
    padding: 0,
    marginLeft: theme.spacing(0.25),
    font: 'inherit',
    fontWeight: 700,
    color: '#0F8461',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
      textUnderlineOffset: 3,
    },
  },
  detailRow: {
    display: 'grid',
    gridTemplateColumns: SUBSCRIPTION_ROW_GRID,
    columnGap: theme.spacing(1.75),
    alignItems: 'center',
    padding: theme.spacing(1.75, 3.25),
    backgroundColor: '#F7F9F8',
    borderTop: '1px solid #EEF2F0',
  },
  detailProductCell: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1.375),
    minWidth: 0,
  },
  detailLabel: {
    fontSize: 13.5,
    fontWeight: 500,
    color: '#16141D',
  },
  detailUnits: {
    marginTop: theme.spacing(0.25),
    fontSize: 12.5,
    color: '#5B6661',
  },
  // Days-remaining warning in detail rows — only rendered when the end date is close.
  detailDays: {
    marginTop: theme.spacing(0.25),
    fontSize: 12.5,
    fontWeight: 600,
    color: '#524409',
  },
  detailRenewal: {
    fontSize: 13.5,
    fontWeight: 500,
    color: '#16141D',
  },
  detailCycle: {
    fontSize: 13.5,
    fontWeight: 500,
    color: '#16141D',
  },
  detailAutoRenew: {
    fontSize: 13,
    fontWeight: 600,
  },
}));

const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;

// "N days remaining" is a warning — only surfaced when the renewal/end date is near.
const DAYS_REMAINING_WARNING_THRESHOLD = 60;

// TestOps G3 rows count Full licences from metadata, every other feature its `quota`.
const getTotalQuota = (subscriptions: Subscription[]) => (
  subscriptions.reduce((total, subscription) => total + getDisplayQuota(subscription), 0)
);

const getEarliestExpirySubscription = (subscriptions: Subscription[]) => (
  [...subscriptions].sort((first, second) => {
    const firstTime = first.expiryDate
      ? new Date(first.expiryDate).getTime()
      : Number.MAX_SAFE_INTEGER;
    const secondTime = second.expiryDate
      ? new Date(second.expiryDate).getTime()
      : Number.MAX_SAFE_INTEGER;
    return firstTime - secondTime;
  })[0]
);

const isPaidSubscription = (subscription: Subscription) => (
  !isTrialSubscription(subscription)
  && subscription.subscriptionType !== 'FREE'
);

export const SubscriptionTableRow = (
  {
    subscriptions,
    accountManaged,
    trackSubscriptionAction,
  }: {
    subscriptions: Subscription[];
    // Account is in the LaunchDarkly direct-sale list — every subscription renders as
    // contract-managed: auto-renew locked (with lock icon) + contract renewal note.
    accountManaged: boolean;
    trackSubscriptionAction?: (payload: TrackingPayload) => void;
  },
) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { navigate, replaceQuery } = useNavigate();
  const { queryDictionary } = useQuery();

  const paidSubscriptions = subscriptions.filter(isPaidSubscription);
  const trialSubscriptions = subscriptions.filter(isTrialSubscription);
  const selfServePaidSubscriptions = paidSubscriptions.filter(isSelfServePaidSubscription);
  const directSalePaidSubscriptions = paidSubscriptions.filter(
    subscription => subscription.secondarySource === SecondarySource.DIRECT_SALE,
  );
  const otherPaidSubscriptions = paidSubscriptions.filter(
    subscription => !isSelfServePaidSubscription(subscription)
      && subscription.secondarySource !== SecondarySource.DIRECT_SALE,
  );
  const displaySubscription = selfServePaidSubscriptions[0]
    ?? paidSubscriptions[0]
    ?? trialSubscriptions[0]
    ?? subscriptions[0];
  const detailPaidSubscriptions = [
    ...selfServePaidSubscriptions,
    ...otherPaidSubscriptions,
    ...directSalePaidSubscriptions,
  ].filter(subscription => subscription.id !== displaySubscription.id);
  const trialSubscription = getEarliestExpirySubscription(trialSubscriptions);
  const paidQuota = getTotalQuota(paidSubscriptions);
  const trialQuota = getTotalQuota(trialSubscriptions);
  const hasPaidSubscription = paidQuota > 0;
  const hasTrialSubscription = trialQuota > 0;
  const isPrimaryTrial = !hasPaidSubscription && hasTrialSubscription;
  const hasSubscriptionBreakdown = hasPaidSubscription
    && (detailPaidSubscriptions.length > 0 || hasTrialSubscription);
  // Legacy True Team bundles add-ons (subscription metadata) — expandable like a breakdown.
  const isLegacyTrueTeam = hasPaidSubscription
    && displaySubscription.feature === Feature.TRUE_TEAM;
  const isExpandable = hasSubscriptionBreakdown || isLegacyTrueTeam;
  const canAddSeats = !isLegacyTrueTeam
    && isSelfServePaidSubscription(displaySubscription)
    && getFeaturePrefix(displaySubscription.feature) !== 'Unknown';
  const isManaged = accountManaged
    || displaySubscription.secondarySource === SecondarySource.DIRECT_SALE;
  const isAutoRenewEnabled = hasPaidSubscription
    && (displaySubscription?.autoRenewEnabled ?? false)
    && !displaySubscription?.canceledAt;
  const showAutoRenew = displaySubscription.source === SubscriptionSource.RECURLY
    && hasPaidSubscription;
  const isMonthly = displaySubscription.billingInterval === PlanInterval.MONTH;
  const showSwitchToAnnual = isMonthly
    && !isManaged
    && isSelfServePaidSubscription(displaySubscription);
  const showAddSeats = canAddSeats && !showSwitchToAnnual;
  // Legacy plans can't self-serve seat changes — route them to sales instead.
  const showContactSales = isLegacyTrueTeam;

  const [autoRenew, setAutoRenew] = useState(isAutoRenewEnabled);
  const [reEnableDialogOpen, setReEnableDialogOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setAutoRenew(isAutoRenewEnabled);
  }, [
    displaySubscription?.autoRenewEnabled,
    displaySubscription?.canceledAt,
    displaySubscription?.id,
    isAutoRenewEnabled,
  ]);

  const getQuotaUnitLabel = (feature: Feature, quota: number) => {
    if (isTestCloudFeature(feature)) {
      return quota === 1 ? 'session' : 'sessions';
    }
    if (
      feature === Feature.TRUE_TEAM
      || feature === Feature.TRUE_ENTERPRISE
      || feature === Feature.TRUE_PLATFORM_ONLY
      || feature === Feature.TRUE_AUTOMATION
    ) {
      return quota === 1 ? 'seat' : 'seats';
    }
    return quota === 1 ? 'license' : 'licenses';
  };

  const getQuotaText = (quota: number, feature = displaySubscription.feature) => (
    `${quota} ${getQuotaUnitLabel(feature, quota)}`
  );

  const getPlanName = (feature: Feature) => {
    switch (feature) {
      case Feature.TRUE_AUTOMATION:
        return intl.formatMessage({ id: 'page.plan.v2.true.automation.title' });
      case Feature.TRUE_PLATFORM_ONLY:
        return intl.formatMessage({ id: 'page.plan.v2.true.platform.title' });
      case Feature.TRUE_TEAM:
        return 'True Team';
      case Feature.TRUE_ENTERPRISE:
        return 'True Enterprise';
      default:
        return intl.formatMessage({
          id: `feature.${feature.toLowerCase()}.product.name`,
        });
    }
  };

  const getDaysRemaining = (subscription: Subscription) => (subscription.expiryDate
    ? Math.max(
      0,
      Math.floor(
        (new Date(subscription.expiryDate).getTime() - Date.now()) / MILLISECONDS_PER_DAY,
      ),
    )
    : 0);

  const getBillingCycleLabel = (subscription: Subscription) => {
    switch (subscription.billingInterval) {
      case PlanInterval.YEAR:
        return intl.formatMessage({ id: 'page.subscription.billing.interval.year.short' });
      case PlanInterval.MONTH:
        return intl.formatMessage({ id: 'page.subscription.billing.interval.month.short' });
      default:
        return subscription.billingInterval ?? '-';
    }
  };

  const getSubscriptionTrackingPayload = (
    subscription: Subscription = displaySubscription,
    payload: TrackingPayload = {},
  ): TrackingPayload => ({
    field: 'current_subscription',
    object: Feature.toString(subscription.feature),
    product_id: getProductId(subscription.feature),
    subscription_id: subscription.id,
    recurly_subscription_id: subscription.recurlySubscriptionId,
    billing_interval: subscription.billingInterval,
    quantity: subscription.quota,
    group_quantity: paidQuota + trialQuota,
    paid_quantity: paidQuota,
    trial_quantity: trialQuota,
    source: subscription.source,
    auto_renew: autoRenew,
    ...payload,
  });

  const handleAddSeats = () => {
    trackSubscriptionAction?.(getSubscriptionTrackingPayload(displaySubscription, {
      action: 'add_seats',
    }));

    const prefix = getFeaturePrefix(displaySubscription.feature);
    navigate(
      plan.path,
      replaceQuery({
        ...queryDictionary(),
        [PLAN_SUBSCRIPTION_ID_QUERY_PARAM]: displaySubscription.id,
        [`${prefix}Quota`]: 1,
        [`${prefix}Cycle`]: displaySubscription.billingInterval,
      }),
    );
  };

  const handleSwitchToAnnual = () => {
    const feature = Feature.fromString(displaySubscription.feature.toUpperCase());
    if (!feature) return;

    trackSubscriptionAction?.(getSubscriptionTrackingPayload(displaySubscription, {
      action: 'switch_to_annual',
    }));

    const prefix = Feature.getFeaturePrefix(feature);
    navigate(
      plan.path,
      replaceQuery({
        ...queryDictionary(),
        [PLAN_SUBSCRIPTION_ID_QUERY_PARAM]: displaySubscription.id,
        // Pure cycle switch: quota param means seats to ADD — 0 keeps current seats,
        // the explicit YEAR cycle marks the SKU engaged as a monthly→annual switch.
        [`${prefix}Quota`]: 0,
        [`${prefix}Cycle`]: PlanInterval.YEAR,
        // Carry the self-serve sub's Recurly uuid so the switch routes to THAT exact sub.
        ...(displaySubscription.recurlySubUuId
          ? { [getRecurlySubUuIdPathParam(feature)]: displaySubscription.recurlySubUuId }
          : {}),
      }),
    );
  };

  const handleContactSales = () => {
    trackSubscriptionAction?.(getSubscriptionTrackingPayload(displaySubscription, {
      action: 'contact_sales',
    }));
  };

  const handleToggleExpand = () => {
    const nextExpanded = !expanded;
    trackSubscriptionAction?.(getSubscriptionTrackingPayload(displaySubscription, {
      field: 'subscription_breakdown',
      object: 'view_more',
      action: nextExpanded ? 'expand' : 'collapse',
    }));
    setExpanded(nextExpanded);
  };

  const handleOpenReEnableDialog = () => {
    trackSubscriptionAction?.(getSubscriptionTrackingPayload(displaySubscription, {
      action: 'open_reenable_auto_renew_dialog',
    }));
    setReEnableDialogOpen(true);
  };

  const handleReEnableAutoRenew = () => {
    if (!displaySubscription.recurlySubscriptionId) return;
    dispatch(fromSubscription.doToggleAutoRenew(displaySubscription.recurlySubscriptionId))
      .then(unwrapResult)
      .then(renewed => {
        setAutoRenew(renewed);
        setReEnableDialogOpen(false);
      })
      .catch(() => {
        enqueueSnackbar(
          intl.formatMessage(
            { id: 'page.subscription.toggle.auto.renew.error' },
            { action: 'turn on' },
          ),
          { variant: 'error' },
        );
      });
  };

  const trackAutoRenewDialogAction = (actionWhenOn: string, actionWhenOff: string) => {
    trackSubscriptionAction?.(getSubscriptionTrackingPayload(displaySubscription, {
      action: autoRenew ? actionWhenOn : actionWhenOff,
    }));
  };

  const renderAutoRenewCell = () => {
    if (!showAutoRenew) {
      return <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#5B6661' }}>&mdash;</Typography>;
    }
    return (
      <Box className={classes.autoRenewCell}>
        {isManaged || !displaySubscription.recurlySubscriptionId
          ? (
            <CustomSwitch disabled checked={autoRenew} />
          )
          : (
            <ToggleRenewal
              productName={displaySubscription.feature.toLowerCase()}
              autoRenewal={autoRenew}
              recurlySubscriptionId={displaySubscription.recurlySubscriptionId}
              setAutoRenew={setAutoRenew}
              expiryDate={displaySubscription.expiryDate}
              billingCycleLabel={getBillingCycleLabel(displaySubscription)}
              onOpenDialog={() => trackAutoRenewDialogAction(
                'open_disable_auto_renew_dialog',
                'open_enable_auto_renew_dialog',
              )}
              onCancelDialog={() => trackAutoRenewDialogAction(
                'cancel_disable_auto_renew_dialog',
                'cancel_enable_auto_renew_dialog',
              )}
              onConfirmDialog={() => trackAutoRenewDialogAction(
                'confirm_disable_auto_renew',
                'confirm_enable_auto_renew',
              )}
            />
          )}
        <Typography
          className={classes.autoRenewState}
          sx={autoRenew
            ? { color: '#5B6661', fontWeight: 500 }
            : { color: '#8A7218', fontWeight: 700 }}
        >
          {autoRenew
            ? intl.formatMessage({ id: 'page.subscription.v2.auto.renew.on' })
            : intl.formatMessage({ id: 'page.subscription.v2.auto.renew.off' })}
        </Typography>
        {isManaged && (
          <Tooltip
            title={intl.formatMessage({ id: 'page.subscription.v3.managed.lock.tooltip' })}
            placement="top"
          >
            <span className={classes.lockIcon}>
              <LockOutlinedIcon sx={{ fontSize: 15 }} />
            </span>
          </Tooltip>
        )}
      </Box>
    );
  };

  const renderNote = () => {
    const showManagedNote = isManaged && autoRenew && hasPaidSubscription;
    const showOffWarning = hasPaidSubscription
      && !autoRenew
      && displaySubscription.source === SubscriptionSource.RECURLY;
    if (!showManagedNote && !showOffWarning) {
      return null;
    }

    return (
      <Box className={`${classes.noteWrap} ${showOffWarning ? classes.noteWarn : classes.noteInfo}`}>
        <Box sx={{ flex: 'none', display: 'inline-flex', mt: '1px', color: showOffWarning ? '#A9761A' : '#8A88A0' }}>
          {showOffWarning
            ? <WarningAmberIcon sx={{ fontSize: 15 }} />
            : <InfoOutlined sx={{ fontSize: 15 }} />}
        </Box>
        <Typography className={classes.noteText}>
          {showOffWarning
            ? (
              <>
                <FormattedMessage id="page.subscription.v2.auto.renew.off.warning.title" />
                {' '}
                <FormattedMessage
                  id="page.subscription.v2.auto.renew.off.warning.desc"
                  values={{
                    date: DateTime.formatDate(intl, displaySubscription.expiryDate),
                  }}
                />
              </>
            )
            : (
              <FormattedMessage id="page.subscription.v3.managed.renewal.note" />
            )}
          {' '}
          {isManaged
            ? (
              <span className={classes.noteHelp}>
                <FormattedMessage
                  id="page.subscription.v3.managed.help.note"
                  values={{
                    email: (
                      <a className={classes.noteHelpLink} href="mailto:success@katalon.com">
                        success@katalon.com
                      </a>
                    ),
                  }}
                />
              </span>
            )
            : (
              <button
                type="button"
                className={classes.reEnableLink}
                onClick={handleOpenReEnableDialog}
              >
                <FormattedMessage id="page.subscription.v2.auto.renew.off.warning.reenable" />
              </button>
            )}
        </Typography>
      </Box>
    );
  };

  const renderDetailRow = (
    {
      key,
      tag,
      tagClass,
      label,
      units,
      renewal,
      daysLabel,
      cycle,
      autoRenewLabel,
      autoRenewColor,
    }: {
      key: string;
      tag: string;
      tagClass: string;
      label: string;
      units: string;
      renewal: string;
      daysLabel?: string;
      cycle: string;
      autoRenewLabel: string;
      autoRenewColor: string;
    },
  ) => (
    <Box className={classes.detailRow} key={key}>
      <Box className={classes.detailProductCell}>
        <span className={`${classes.pill} ${tagClass}`}>{tag}</span>
        <Box sx={{ minWidth: 0 }}>
          <Typography className={classes.detailLabel}>{label}</Typography>
          <Typography className={classes.detailUnits}>{units}</Typography>
        </Box>
      </Box>
      <Box>
        <Typography className={classes.detailRenewal}>{renewal}</Typography>
        {daysLabel && <Typography className={classes.detailDays}>{daysLabel}</Typography>}
      </Box>
      <Typography className={classes.detailCycle}>{cycle}</Typography>
      <Typography className={classes.detailAutoRenew} sx={{ color: autoRenewColor }}>
        {autoRenewLabel}
      </Typography>
      <span />
    </Box>
  );

  // Legacy True Team add-ons live in subscription metadata; names are RecurlyAddOn enum
  // keys (plus KRE, which the FE enum does not list).
  const getLegacyAddOnFeature = (metadataName: string): Feature | undefined => {
    if (metadataName === 'KRE') {
      return Feature.UNLIMITED_ENGINE;
    }
    const addOnCode = (RecurlyAddOn as Record<string, string>)[metadataName];
    return addOnCode ? getFeatureByAddOn(addOnCode) : undefined;
  };

  const getAddOnUnitsLabel = (feature: Feature, quantity: number) => intl.formatMessage(
    {
      id: feature === Feature.UNLIMITED_ENGINE
        ? 'page.subscription.v3.addon.units.nodes'
        : 'page.subscription.v3.addon.units.sessions',
    },
    { count: quantity },
  );

  const renderDetailRows = () => {
    const autoRenewOnLabel = intl.formatMessage({ id: 'page.subscription.v2.auto.renew.on' });
    const autoRenewOffLabel = intl.formatMessage({ id: 'page.subscription.v2.auto.renew.off' });
    const daysRemainingLabel = (subscription: Subscription) => intl.formatMessage(
      { id: 'page.subscription.v2.days.remaining' },
      { days: getDaysRemaining(subscription) },
    );
    // Paid rows only warn when the renewal is near; trial rows always show time left.
    const nearRenewalDaysLabel = (subscription: Subscription) => (
      getDaysRemaining(subscription) <= DAYS_REMAINING_WARNING_THRESHOLD
        ? daysRemainingLabel(subscription)
        : undefined
    );
    const addOnTag = intl.formatMessage({ id: 'page.subscription.v3.pill.addon' });
    const renderLegacyAddOnRow = (
      metadataName: string,
      feature: Feature,
      quantity: number,
      notRenewing: boolean,
    ) => renderDetailRow({
      key: `addon-${metadataName}${notRenewing ? '-canceled' : ''}`,
      tag: addOnTag,
      tagClass: classes.legacyPill,
      label: intl.formatMessage({ id: `feature.${feature.toLowerCase()}.product.name` }),
      units: getAddOnUnitsLabel(feature, quantity),
      renewal: DateTime.formatDate(intl, displaySubscription.expiryDate),
      daysLabel: nearRenewalDaysLabel(displaySubscription),
      cycle: getBillingCycleLabel(displaySubscription),
      autoRenewLabel: notRenewing
        ? intl.formatMessage({ id: 'page.subscription.v2.not.renewing.badge' })
        : (autoRenew && autoRenewOnLabel) || autoRenewOffLabel,
      autoRenewColor: notRenewing || !autoRenew ? '#5B6661' : '#0B6B4E',
    });

    if (isLegacyTrueTeam) {
      return (
        <>
          {(displaySubscription.metadata ?? []).map(metadata => {
            const feature = getLegacyAddOnFeature(metadata.name);
            if (!feature) {
              return null;
            }
            const canceledQuantity = displaySubscription.canceledAddOns?.[metadata.name] ?? 0;
            const currentQuantity = (metadata.value ?? 0) - canceledQuantity;
            return (
              <div key={`addon-${metadata.name}-rows`}>
                {currentQuantity > 0
                  && renderLegacyAddOnRow(metadata.name, feature, currentQuantity, false)}
                {canceledQuantity > 0
                  && renderLegacyAddOnRow(metadata.name, feature, canceledQuantity, true)}
              </div>
            );
          })}
        </>
      );
    }

    return (
      <>
        {[displaySubscription, ...detailPaidSubscriptions]
          .filter(isPaidSubscription)
          .map(subscription => {
            const isDirectSale = subscription.secondarySource === SecondarySource.DIRECT_SALE;
            // The store is not refreshed after a toggle — for the sub the row's switch
            // controls, mirror the live local state instead of the stale store field.
            const subscriptionAutoRenew = subscription.id === displaySubscription.id
              ? autoRenew
              : !!subscription.autoRenewEnabled && !subscription.canceledAt;
            return renderDetailRow({
              key: `${subscription.feature}-${subscription.id}`,
              tag: intl.formatMessage({
                id: isDirectSale
                  ? 'page.subscription.v3.pill.managed'
                  : 'page.subscription.v3.pill.paid',
              }),
              tagClass: isDirectSale ? classes.managedPill : classes.paidPill,
              label: intl.formatMessage({ id: 'page.subscription.v3.detail.paid.label' }),
              units: getQuotaText(getDisplayQuota(subscription), subscription.feature),
              renewal: DateTime.formatDate(intl, subscription.expiryDate),
              daysLabel: nearRenewalDaysLabel(subscription),
              cycle: getBillingCycleLabel(subscription),
              autoRenewLabel: subscriptionAutoRenew ? autoRenewOnLabel : autoRenewOffLabel,
              autoRenewColor: subscriptionAutoRenew ? '#0B6B4E' : '#5B6661',
            });
          })}
        {hasTrialSubscription && trialSubscription && renderDetailRow({
          key: `${trialSubscription.feature}-${trialSubscription.id}-trial`,
          tag: intl.formatMessage({ id: 'page.subscription.v3.pill.trial' }),
          tagClass: classes.trialPill,
          label: intl.formatMessage({ id: 'page.subscription.v3.detail.trial.label' }),
          units: getQuotaText(trialQuota, trialSubscription.feature),
          renewal: intl.formatMessage(
            { id: 'page.subscription.v3.trial.ends' },
            { date: DateTime.formatDate(intl, trialSubscription.expiryDate) },
          ),
          daysLabel: daysRemainingLabel(trialSubscription),
          cycle: '—',
          autoRenewLabel: '—',
          autoRenewColor: '#5B6661',
        })}
      </>
    );
  };

  const seatLabel = hasPaidSubscription
    ? getQuotaText(paidQuota)
    : getQuotaText(trialQuota, trialSubscription?.feature ?? displaySubscription.feature);

  return (
    <Box className={classes.rowContainer}>
      <Box className={classes.rowGrid}>
        {/* Product */}
        <Box className={classes.productCell}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography className={classes.productName}>
              {getPlanName(displaySubscription.feature)}
            </Typography>
            <Box className={classes.productSubtitle}>
              {/* Paid + trial mix replaces the seat label with count pills (5 paid / 2 trial). */}
              {!(hasPaidSubscription && hasTrialSubscription) && (
                <span className={classes.seatLabel}>{seatLabel}</span>
              )}
              {hasPaidSubscription && hasTrialSubscription && (
                <>
                  <span className={`${classes.pill} ${classes.paidPill}`}>
                    <FormattedMessage
                      id="page.subscription.v3.pill.paid.count"
                      values={{ count: paidQuota }}
                    />
                  </span>
                  <span className={`${classes.pill} ${classes.trialPill}`}>
                    <FormattedMessage
                      id="page.subscription.v3.pill.trial.count"
                      values={{ count: trialQuota }}
                    />
                  </span>
                </>
              )}
              {isPrimaryTrial && (
                <span className={`${classes.pill} ${classes.trialPill}`}>
                  <FormattedMessage id="page.subscription.v3.pill.trial" />
                </span>
              )}
              {isLegacyTrueTeam && (
                <span className={`${classes.pill} ${classes.legacyPill}`}>
                  <FormattedMessage id="page.subscription.v3.pill.legacy" />
                </span>
              )}
            </Box>
          </Box>
          {isExpandable && (
            <IconButton
              className={classes.expandButton}
              onClick={handleToggleExpand}
              aria-expanded={expanded}
              aria-label={intl.formatMessage(
                { id: expanded ? 'page.subscription.v3.collapse.aria' : 'page.subscription.v3.expand.aria' },
                { name: getPlanName(displaySubscription.feature) },
              )}
            >
              <KeyboardArrowDownIcon
                sx={{
                  fontSize: 18,
                  transition: 'transform .2s ease',
                  transform: expanded ? 'rotate(180deg)' : 'none',
                }}
              />
            </IconButton>
          )}
        </Box>

        {/* Next renewal / Trial ends */}
        <Box>
          <Typography className={classes.renewalDate}>
            {isPrimaryTrial
              ? intl.formatMessage(
                { id: 'page.subscription.v3.trial.ends' },
                { date: DateTime.formatDate(intl, displaySubscription.expiryDate) },
              )
              : DateTime.formatDate(intl, displaySubscription.expiryDate)}
          </Typography>
          {getDaysRemaining(displaySubscription) <= DAYS_REMAINING_WARNING_THRESHOLD && (
            <Typography className={classes.renewalDays}>
              <FormattedMessage
                id="page.subscription.v2.days.remaining"
                values={{ days: getDaysRemaining(displaySubscription) }}
              />
            </Typography>
          )}
        </Box>

        {/* Billing cycle — trials have no billing cycle, the design shows a dash. */}
        <Typography className={classes.cycleValue}>
          {isPrimaryTrial ? '—' : getBillingCycleLabel(displaySubscription)}
        </Typography>

        {/* Auto-renew */}
        {renderAutoRenewCell()}

        {/* Actions */}
        {showSwitchToAnnual && (
          <Button
            className={classes.actionBtn}
            onClick={handleSwitchToAnnual}
          >
            <FormattedMessage id="page.subscription.v3.switch.to.annual.btn" />
          </Button>
        )}
        {showAddSeats && (
          <Button
            className={classes.actionBtn}
            onClick={handleAddSeats}
            startIcon={<AddIcon sx={{ fontSize: 15 }} />}
          >
            <FormattedMessage id="page.subscription.v3.add.seats.btn" />
          </Button>
        )}
        {showContactSales && (
          <Button
            className={classes.actionBtn}
            component="a"
            href="https://katalon.com/contact-us#contact-us"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleContactSales}
          >
            <FormattedMessage id="page.subscription.v3.contact.sales.btn" />
          </Button>
        )}
        {!showSwitchToAnnual && !showAddSeats && !showContactSales && <span />}
      </Box>

      {renderNote()}

      {isExpandable && (
        <Collapse in={expanded}>
          {renderDetailRows()}
        </Collapse>
      )}

      {/* Re-enable renewal confirm dialog */}
      {hasPaidSubscription && displaySubscription.recurlySubscriptionId && !isManaged && (
        <ToggleRenewalConfirmDialog
          productName={displaySubscription.feature.toLowerCase()}
          autoRenew={autoRenew}
          open={reEnableDialogOpen}
          onClose={() => setReEnableDialogOpen(false)}
          toggleAutoRenew={handleReEnableAutoRenew}
          expiryDate={displaySubscription.expiryDate}
          billingCycleLabel={getBillingCycleLabel(displaySubscription)}
          onCancel={() => trackSubscriptionAction?.(
            getSubscriptionTrackingPayload(displaySubscription, {
              action: 'cancel_reenable_auto_renew_dialog',
            }),
          )}
          onConfirm={() => trackSubscriptionAction?.(
            getSubscriptionTrackingPayload(displaySubscription, {
              action: 'confirm_reenable_auto_renew',
            }),
          )}
        />
      )}
    </Box>
  );
};
